/* eslint-disable react-hooks/exhaustive-deps */
import { FunctionComponent as F, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from 'react-redux';
import { useAppDispatch } from "../../core/store/hooks";
import { IState } from "../../core/store/store";
import { retrieveBrandsAsync, submitNewProductAsync } from "../../core/store/products/products.thunks";
import ControlledCreatableAutocomplete from "../UI/Input/ControlledCreatableAutocomplete";
import ControlledAutocomplete from "../UI/Input/ControlledAutocomplete";
import {
  Stack,
  Typography,
  Dialog,
  DialogTitle ,
  DialogContent,
  TextField,
  IconButton,
  Link,
  Box,
  Grid }from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import Button from "../UI/Button/Button";
import { IOption, IUnit } from "@protoplan/types";
import { validateDose, validateProductAmount } from "../../utils/validate";
import { TBrand } from "../../core/types/product";
import ClearIcon from '@mui/icons-material/Clear';
import { getSupps } from "../../core/http/product";
import unitsInstance from "../../core/units";


const NewProduct: F = () =>
{
  const navigate = useNavigate();
  const appDispatch = useAppDispatch();

  const username = useSelector((state: IState) => state.user.username);
  const products = useSelector((state: IState) => state.products.products);
  const undeliverable = products?.filter(p => !p.deliverableListingsForUser);
  const brands = useSelector((state: IState) => state.products.brands);
  const brandsToOptions = (brands: TBrand[]) => brands.map(brand =>
    ({ value: brand.brandId, label: brand.name }));
  const [name, setName] = useState("");
  const [brandOptions, setBrandOptions] = useState(brandsToOptions(brands));
  useEffect(() => setBrandOptions(brandsToOptions(brands)), [brands]);
  const [currentBrandOption, setCurrentBrandOption] = useState<IOption | null>(null);
  const [amount, setAmount] = useState("");
  const [newUnitConversions, setNewUnitConversions] = useState<Array<{
    unitConversionId: number,
    fromUnitId?: number,
    nFromUnit: string,
    toUnitId?: number,
    nToUnit: string,
    required?: boolean }>>([]);

  const [genericUnits, setGenericUnits] = useState<IUnit[]>([]);
  const unitsToOptions = (units: IUnit[]) => units.map(u => ({ label: u.name, value: u.unitId }));
  const unitOptions = unitsToOptions(genericUnits);
  const [currentAmountUnitOption, setCurrentAmountUnitOption] = useState<IOption | null>(null);
  const [recDose, setRecDose] = useState("");
  const [recDosesPerDay, setRecDosesPerDay] = useState("");
  const [currentRecDoseUnitOption, setCurrentRecDoseUnitOption] = useState<IOption | null>(null);
  const [activeIngredient, setActiveIngredient] = useState("");
  const [
    currentActiveIngredientUnitOption,
    setCurrentActiveIngredientUnitOption] = useState<IOption | null>(null);
  const [suppOptions, setSuppOptions] = useState<IOption[]>([]);
  useEffect(() =>
  {
    getSupps()
      .then(supps => supps && setSuppOptions(supps.map(s => ({ value: s.suppId, label: s.name }))));

    unitsInstance.getUnits()
      .then(us => setGenericUnits(us));
  }, []);
  const [currentSuppOption, setCurrentSuppOption] = useState<IOption | null>(null);

  useEffect(() =>
  {
    if(!brands.length)
      appDispatch(retrieveBrandsAsync());
  }, []);

  const convertible = async(unitId1: number, unitId2: number) =>
    unitId1 === unitId2 ||
    await unitsInstance.getFactor(unitId1, unitId2)

  const newUnitConversion = {
    unitConversionId: newUnitConversions.length * -1,  // new unit conversions have unitConversionIds <= 0
    nFromUnit: "1",
    nToUnit: "1" };

  // Set unit conversions
  useEffect(() =>
  {
    if(!unitsInstance)
      return;

    // Set to non-required
    newUnitConversions.forEach((nUc, i) =>
    {
      if(!nUc.required)
        return;

      const required = unitsMatchConversion(nUc);
      if(!required)
      {
        const copy = [...newUnitConversions];
        copy[i].required =false;
        setNewUnitConversions(copy);
      }
    });

    if(
      !currentRecDoseUnitOption || !currentAmountUnitOption ||
      currentRecDoseUnitOption.value === currentAmountUnitOption.value)
    {
      return;
    }

    convertible(currentRecDoseUnitOption.value, currentAmountUnitOption.value)
      .then(c =>
      {
        if(c)
          return;

        // Required?
        if(!newUnitConversions.find(c => unitsMatchConversion(c)))
        {
          return setNewUnitConversions(newUnitConversions.concat({
            ...newUnitConversion,
            fromUnitId: currentAmountUnitOption.value,
            toUnitId: currentRecDoseUnitOption.value,
            required: true }));
        }

        const newUnitConversionsCopy = [...newUnitConversions.map(c => ({ ...c }))];
        const matchingConversion = newUnitConversionsCopy.find(c => unitsMatchConversion(c));
        if(!matchingConversion)
          return;
        matchingConversion.required = true;

        setNewUnitConversions(newUnitConversionsCopy);
      });

  }, [currentAmountUnitOption, currentRecDoseUnitOption]);

  const unitsMatchConversion = (
    unitConversion: { fromUnitId?: number, toUnitId?: number },
    unitId1 = currentRecDoseUnitOption?.value,
    unitId2 = currentAmountUnitOption?.value) =>
    unitId1 && unitId2 && unitConversion.fromUnitId && unitConversion.fromUnitId && (
      (unitConversion.fromUnitId === unitId1 && unitConversion.toUnitId === unitId2) ||
      (unitConversion.fromUnitId === unitId2 && unitConversion.toUnitId === unitId1));


  if(!username)
  {
    navigate("../", { relative: 'path' });
    return <></>;
  }

  const handleChangeNFromUnit = (index: number, text: string) =>
  {
    const copy = [...newUnitConversions];
    copy[index].nFromUnit = text;
    setNewUnitConversions(copy);
  }

  const handleChangeFromUnit = (index: number, option: IOption | null) =>
  {
    const copy = [...newUnitConversions];
    copy[index].fromUnitId = option?.value;
    setNewUnitConversions(copy);
  }

  const handleChangeNToUnit = (index: number, text: string) =>
  {
    const copy = [...newUnitConversions];
    copy[index].nToUnit = text;
    setNewUnitConversions(copy);
  }

  const handleChangeToUnit = (index: number, option: IOption | null) =>
  {
    const copy = [...newUnitConversions];
    copy[index].toUnitId = option?.value;
    setNewUnitConversions(copy);
  }

  const incomplete = (
    !name ||
    !currentSuppOption ||
    !currentBrandOption ||
    !currentAmountUnitOption ||
    !validateProductAmount(amount) ||
    !(validateDose(recDose) || recDose === "") ||
    !(validateDose(recDosesPerDay) || recDosesPerDay === "")  ||
    !(validateDose(activeIngredient) || activeIngredient === "") ||
    !newUnitConversions
      .filter(uc => uc.required)
      .every(uc => uc.toUnitId && parseFloat(uc.nFromUnit) > 0 && uc.fromUnitId && parseFloat(uc.nToUnit) > 0));

  const handleSubmit = async() =>
  {
    if(incomplete)
      return;

    const newConversionsWithFactor = newUnitConversions
      .filter(c => c.toUnitId !== undefined && c.fromUnitId !== undefined && c.nFromUnit && c.nToUnit)
      .map(c => ({
        ...c,
        fromUnitId: Number(c.fromUnitId),
        toUnitId: Number(c.toUnitId),
        factor: Number(c.nToUnit) / Number(c.nFromUnit) }));

    // Submit product
    const product = await appDispatch(submitNewProductAsync({
      name,
      brandId: currentBrandOption.value,
      brandName: currentBrandOption.label,
      suppId: currentSuppOption.value,
      suppName: currentSuppOption.label,
      amount: Number(amount),
      amountUnitId: currentAmountUnitOption.value,
      recDose: Number(recDose) || undefined,
      recDoseUnitId: currentRecDoseUnitOption?.value,
      recDosesPerDay: Number(recDosesPerDay) || undefined,
      activeIngredientPerRecDose: Number(activeIngredient) || undefined,
      activeIngredientPerRecDoseUnitId: currentActiveIngredientUnitOption?.value }, newConversionsWithFactor));

    if(!product)
      return;

    navigate("../" + product?.productId + "/listings", { relative: 'path' });
  };


  return (
    <Dialog
      maxWidth='md'
      fullWidth
      PaperProps={{ sx: { p: '15px' }}}
      open={true}
      onClose={() => navigate("../../", { relative: 'path' })}>
      <IconButton
        disableRipple
        onClick={() => navigate("../", { relative: 'path' })}
        sx={{ display: 'inline-flex', float: 'left', width: '80px' }}>
        <ArrowBackIosNewIcon sx={{ mt: 3, ml: 1, fontSize: 65, color: 'lightGray' }}/>
      </IconButton>

      <DialogTitle sx={{ ml: 3, fontSize: '30px' }}>Add a new product</DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ width: '750px', pt: 1 }}>

          {undeliverable?.length ?
            <>
              <Typography>Did you mean... ?</Typography>
              {undeliverable.map(p =>
                <Link
                  style={{ cursor: 'pointer', marginBlock: 3 }}
                  variant='body2'
                  noWrap
                  onClick={() => navigate(`../${p.productId}/listings/new`, { relative: 'path' })}>
                  {`${p.brandName} ${p.productName} ${p.amount} ${p.amountUnit}`}
                </Link>)}
            </> :
            <></>}

          <TextField
            id='ProductName'
            label='Name on packaging*'
            variant='outlined'
            size='small'
            sx={{ width: '750px' }}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setName(event.target.value)} />

          <Stack direction='row' spacing={1}>
            <ControlledCreatableAutocomplete
              id='BrandName'
              label='Brand*'
              width='70%'
              currentOption={currentBrandOption}
              handleChangeCurrentOption={setCurrentBrandOption}
              options={brandOptions} />
            <Box width='30%'>
              <ControlledCreatableAutocomplete
                label='Supplement or food type*'
                currentOption={currentSuppOption}
                handleChangeCurrentOption={setCurrentSuppOption}
                options={suppOptions}/>
            </Box>
          </Stack>

          <Stack direction='row' spacing={1}>
            <TextField
              id='Amount'
              label='Amount*'
              value={amount}
              variant='outlined'
              size='small'
              sx={{ width: '45%' }}
              error={!(validateProductAmount(amount) || amount === "")}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setAmount(event.target.value)} />
            <ControlledAutocomplete
              id='AmountUnit'
              label='Unit*'
              width='35%'
              currentOption={currentAmountUnitOption}
              onChangeCurrentOption={setCurrentAmountUnitOption}
              options={unitOptions} />
            <Box sx={{ width: '20%' }}/>
          </Stack>

          <Typography variant='h6' marginTop={'30px'}>
            {"Optional"}
          </Typography>

          <Stack direction='row' spacing={1}>
            <TextField
              id='RecDose'
              label='Recommended dose'
              value={recDose}
              variant='outlined'
              size='small'
              sx={{ width: '45%' }}
              error={!(validateDose(recDose) || recDose === "")}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setRecDose(event.target.value)} />
            <ControlledAutocomplete
              id='RecDoseUnit'
              label='Unit'
              width='35%'
              currentOption={currentRecDoseUnitOption}
              onChangeCurrentOption={setCurrentRecDoseUnitOption}
              options={unitOptions} />
            <TextField
              id='RecDosesPerDay'
              label='Times per day'
              value={recDosesPerDay}
              variant='outlined'
              size='small'
              sx={{ width: '20%' }}
              error={!(validateDose(recDosesPerDay) || recDosesPerDay === "")}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setRecDosesPerDay(event.target.value)} />
          </Stack>

          <Stack direction='row' spacing={1}>
            <TextField
              id='ActiveIngredientPerRecDose'
              label='Active ingredient per recommended dose'
              value={activeIngredient}
              variant='outlined'
              size='small'
              sx={{ width: '45%' }}
              error={!(validateDose(activeIngredient) || activeIngredient === "")}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setActiveIngredient(event.target.value)} />
            <ControlledAutocomplete
              id='ActiveIngredientPerRecDoseUnit'
              label='Unit'
              width='35%'
              currentOption={currentActiveIngredientUnitOption}
              onChangeCurrentOption={setCurrentActiveIngredientUnitOption}
              options={unitOptions} />
            <Box sx={{ width: '20%' }}/>
          </Stack>

          {!newUnitConversions.length ?
            <Stack direction='row' spacing={1} key={"add"}>
              <Link
                variant='body2'
                underline='hover'
                style={{ cursor: 'pointer' }}
                onClick={() => setNewUnitConversions([newUnitConversion])} >
                <i>{"Add unit conversions for this product"}</i>
              </Link>
              <></>
            </Stack>:
            <>
              <Typography variant='h6' marginTop={'30px'}>
                {"Unit conversions"}
              </Typography>
              {newUnitConversions.map((nUc, i) =>
              {
                return (
                  <Stack direction='row' spacing={1} key={i} alignItems='center'>
                    <TextField
                      value={nUc.nFromUnit}
                      variant='outlined'
                      size='small'
                      sx={{ width: '200px' }}
                      error={!(validateProductAmount(nUc.nFromUnit) || !nUc.nFromUnit)}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        handleChangeNFromUnit(i, event.target.value)} />
                    <ControlledAutocomplete
                      label={'Unit 1' + (nUc.required ? '*' : '')}
                      width='80%'
                      currentOption={unitOptions.find(u => u.value === nUc.fromUnitId) || null}
                      onChangeCurrentOption={(option: IOption | null) => handleChangeFromUnit(i, option)}
                      disabled={nUc.required}
                      options={unitOptions} />
                    <Typography variant='body1'>
                      {"="}
                    </Typography>
                    <TextField
                      value={nUc.nToUnit}
                      variant='outlined'
                      size='small'
                      sx={{ width: '200px' }}
                      error={!(validateProductAmount(nUc.nToUnit) || !nUc.nToUnit)}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        handleChangeNToUnit(i, event.target.value)} />
                    <ControlledAutocomplete
                      label={'Unit 2' + (nUc.required ? '*' : '')}
                      width='80%'
                      currentOption={unitOptions.find(u => u.value === nUc.toUnitId) || null}
                      onChangeCurrentOption={(option: IOption | null) => handleChangeToUnit(i, option)}
                      disabled={nUc.required}
                      options={unitOptions} />
                    <ClearIcon
                      sx={{
                        cursor: nUc.required ? 'auto' : 'pointer',
                        color: nUc.required ? 'transparent' : 'lightGrey' }}
                      onClick={() =>
                      {
                        if(nUc.required)
                          return;

                        const copy = [...newUnitConversions];
                        copy.splice(i, 1)
                        setNewUnitConversions(copy);
                      }}/>
                  </Stack>)
              })}
              <Stack direction='row' spacing={1} key={"add"}>
                <Link
                  variant='body2'
                  underline='hover'
                  style={{ cursor: 'pointer', display: 'inline-block' }}
                  onClick={() => setNewUnitConversions(newUnitConversions.concat(newUnitConversion))} >
                  <i>{"Add another unit conversion"}</i>
                </Link>
                <></>
              </Stack>
            </>}

          <Grid container direction="column" alignItems="center" spacing={2}>
            <Grid item>
              <Button
                id='Submit'
                label='Submit'
                onClick={handleSubmit}
                disabled={incomplete}/>
            </Grid>
          </Grid>

        </Stack>

      </DialogContent>
    </Dialog>);
};


export default NewProduct;
